<template>
  <v-container fluid>
    <v-row>
      <v-col cols="12">
        <h1 class="mb-2">Organization Insights</h1>
        <p class="grey--text text--darken-1">
          Review event performance by organization and state.
        </p>
      </v-col>
    </v-row>

    <!-- Filters -->
    <v-card class="mb-4">
      <v-card-title>
        Filters
        <v-spacer />
      </v-card-title>

      <v-card-text>
        <v-row>
          <v-col cols="12" md="3">
            <v-menu
              v-model="startMenu"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              min-width="auto"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="filters.startDate"
                  label="Start Date"
                  readonly
                  outlined
                  dense
                  clearable
                  v-bind="attrs"
                  v-on="on"
                />
              </template>

              <v-date-picker
                v-model="filters.startDate"
                @input="startMenu = false"
              />
            </v-menu>
          </v-col>

          <v-col cols="12" md="3">
            <v-menu
              v-model="endMenu"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              min-width="auto"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="filters.endDate"
                  label="End Date"
                  readonly
                  outlined
                  dense
                  clearable
                  v-bind="attrs"
                  v-on="on"
                />
              </template>

              <v-date-picker
                v-model="filters.endDate"
                @input="endMenu = false"
              />
            </v-menu>
          </v-col>

          <v-col cols="12" md="3">
            <v-select
              v-model="filters.quarterKey"
              :items="quarterOptions"
              label="Quarter"
              outlined
              dense
              clearable
            />
          </v-col>

          <v-col cols="12" md="3">
            <v-select
              v-model="filters.organizationState"
              :items="stateOptions"
              label="State"
              outlined
              dense
              clearable
            />
          </v-col>

<!--           <v-col cols="12" md="3">
            <v-select
              v-model="filters.organizationType"
              :items="organizationTypeOptions"
              label="Organization Type"
              outlined
              dense
              clearable
            />
          </v-col> -->

          <v-col cols="12" md="6">
            <v-text-field
              v-model="filters.organizationSearch"
              label="Organization Search"
              outlined
              dense
              clearable
              append-icon="mdi-magnify"
            />
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- KPI Cards -->
    <v-row class="mb-4">
      <v-col cols="12" md="3">
        <v-card>
          <v-card-text>
            <div class="caption grey--text">Organizations</div>
            <div class="headline font-weight-bold">
              {{ organizationRows.length }}
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" md="3">
        <v-card>
          <v-card-text>
            <div class="caption grey--text">Events</div>
            <div class="headline font-weight-bold">
              {{ filteredEventRows.length }}
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" md="3">
        <v-card>
          <v-card-text>
            <div class="caption grey--text">Total Event Value</div>
            <div class="headline font-weight-bold">
              {{ formatCurrency(totalEventValue) }}
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" md="3">
        <v-card>
          <v-card-text>
            <div class="caption grey--text">Avg Event Value</div>
            <div class="headline font-weight-bold">
              {{ formatCurrency(averageEventValue) }}
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Table -->
    <v-card>
      <v-card-title>
        Organization Summary
        <v-spacer />

        <v-text-field
          v-model="tableSearch"
          append-icon="mdi-magnify"
          label="Search Table"
          single-line
          hide-details
          dense
          clearable
        />
      </v-card-title>

      <v-data-table
        :headers="headers"
        :items="organizationRows"
        :search="tableSearch"
        :loading="loading"
        :items-per-page="25"
        sort-by="totalEventValue"
        sort-desc
        class="elevation-1"
      >
        <template v-slot:item.totalEventValue="{ item }">
          {{ formatCurrency(item.totalEventValue) }}
        </template>

        <template v-slot:item.averageEventValue="{ item }">
          {{ formatCurrency(item.averageEventValue) }}
        </template>

        <template v-slot:item.lastEventDate="{ item }">
          {{ formatDate(item.lastEventDate) }}
        </template>
      </v-data-table>
    </v-card>

    <!-- Raw Event Details -->
    <v-card class="mt-4">
      <v-card-title>
        Event Detail
      </v-card-title>

      <v-data-table
        :headers="eventHeaders"
        :items="filteredEventRows"
        :loading="loading"
        :items-per-page="10"
        sort-by="eventDate"
        sort-desc
        dense
      >
        <template v-slot:item.eventDate="{ item }">
          {{ formatDate(item.eventDate) }}
        </template>

        <template v-slot:item.eventReportTotal="{ item }">
          {{ formatCurrency(item.eventReportTotal) }}
        </template>
      </v-data-table>
    </v-card>
  </v-container>
</template>

<script>
import moment from "moment";
import { portalsApp } from "@/firebase";

export default {
  name: "OrganizationInsights",

  data() {
    return {
      loading: false,
      startMenu: false,
      endMenu: false,
      tableSearch: "",

      filters: {
        startDate: null,
        endDate: null,
        quarterKey: null,
        organizationState: null,
        //organizationType: null,
        organizationSearch: "",
      },

      eventRows: [],

      headers: [
        { text: "Organization", value: "organizationName" },
        //{ text: "Type", value: "organizationType" },
        { text: "State", value: "organizationState" },
        { text: "Events", value: "eventCount" },
        { text: "Total Event Value", value: "totalEventValue" },
        { text: "Avg Event Value", value: "averageEventValue" },
        { text: "Winners", value: "totalWinners" },
        { text: "Last Event", value: "lastEventDate" },
      ],

      eventHeaders: [
        { text: "Event Date", value: "eventDate" },
        { text: "Organization", value: "organizationName" },
        { text: "Event Name", value: "eventName" },
        //{ text: "Type", value: "organizationType" },
        { text: "State", value: "organizationState" },
        { text: "Address", value: "organizationAddress" },
        { text: "Event Total", value: "eventReportTotal" },
        { text: "Winners", value: "numberOfWinners" },
      ],
    };
  },

  computed: {
    quarterOptions() {
      var options = [];
      var currentYear = moment().year();
      var currentQuarter = moment().quarter();

      for (var year = 2019; year <= currentYear; year++) {
        var maxQuarter = year === currentYear ? currentQuarter : 4;

        for (var quarter = 1; quarter <= maxQuarter; quarter++) {
          options.push({
            text: year + " Q" + quarter,
            value: year + "-Q" + quarter,
          });
        }
      }

      return options.reverse();
    },

    filteredEventRows() {
      var output = this.eventRows.slice();

      if (this.filters.organizationState) {
        output = output.filter((row) => {
          return row.organizationState === this.filters.organizationState;
        });
      }

/*       if (this.filters.organizationType) {
        output = output.filter((row) => {
          return row.organizationType === this.filters.organizationType;
        });
      } */

      if (this.filters.organizationSearch) {
        var search = this.filters.organizationSearch.toLowerCase();

        output = output.filter((row) => {
          return (
            String(row.organizationName || "").toLowerCase().indexOf(search) >
              -1 ||
            String(row.eventName || "").toLowerCase().indexOf(search) > -1 ||
            String(row.organizationAddress || "")
              .toLowerCase()
              .indexOf(search) > -1
          );
        });
      }

      if (this.filters.quarterKey) {
        var quarterParts = this.filters.quarterKey.split("-Q");
        var year = Number(quarterParts[0]);
        var quarter = Number(quarterParts[1]);

        var quarterStart = moment()
          .year(year)
          .quarter(quarter)
          .startOf("quarter");

        var quarterEnd = moment()
          .year(year)
          .quarter(quarter)
          .endOf("quarter");

        output = output.filter((row) => {
          var parsed = this.parseDate(row.eventDate);
          if (!parsed || !parsed.isValid()) return false;
          return parsed.isBetween(quarterStart, quarterEnd, "day", "[]");
        });

        return output;
      }

      if (this.filters.startDate) {
        output = output.filter((row) => {
          var parsed = this.parseDate(row.eventDate);
          if (!parsed || !parsed.isValid()) return false;
          return parsed.isSameOrAfter(this.filters.startDate, "day");
        });
      }

      if (this.filters.endDate) {
        output = output.filter((row) => {
          var parsed = this.parseDate(row.eventDate);
          if (!parsed || !parsed.isValid()) return false;
          return parsed.isSameOrBefore(this.filters.endDate, "day");
        });
      }

      return output;
    },

    organizationRows() {
      var map = {};

      this.filteredEventRows.forEach((row) => {
        var key = String(row.organizationName || "Unknown").toLowerCase();

        if (!map[key]) {
          map[key] = {
            organizationName: row.organizationName || "Unknown",
            //organizationType: row.organizationType || "Unknown",
            organizationState: row.organizationState || "Unknown",
            eventCount: 0,
            totalEventValue: 0,
            averageEventValue: 0,
            totalWinners: 0,
            lastEventDate: "",
          };
        }

        map[key].eventCount += 1;
        map[key].totalEventValue += this.toNumber(row.eventReportTotal);
        map[key].totalWinners += this.toNumber(row.numberOfWinners);

        if (
          !map[key].lastEventDate ||
          this.parseDate(row.eventDate).isAfter(this.parseDate(map[key].lastEventDate))
        ) {
          map[key].lastEventDate = row.eventDate;
        }
      });

      return Object.keys(map).map((key) => {
        var item = map[key];

        if (item.eventCount > 0) {
          item.averageEventValue = item.totalEventValue / item.eventCount;
        }

        return item;
      });
    },

    stateOptions() {
      var values = [];

      this.eventRows.forEach((row) => {
        if (
          row.organizationState &&
          values.indexOf(row.organizationState) === -1
        ) {
          values.push(row.organizationState);
        }
      });

      return values.sort();
    },

/*     organizationTypeOptions() {
      var values = [];

      this.eventRows.forEach((row) => {
        if (
          row.organizationType &&
          values.indexOf(row.organizationType) === -1
        ) {
          values.push(row.organizationType);
        }
      });

      return values.sort();
    }, */

    totalEventValue() {
      var total = 0;

      this.filteredEventRows.forEach((row) => {
        total += this.toNumber(row.eventReportTotal);
      });

      return total;
    },

    averageEventValue() {
      if (!this.filteredEventRows.length) return 0;

      return this.totalEventValue / this.filteredEventRows.length;
    },
  },

  mounted() {
    this.loadEventReports();
  },

  methods: {
    async loadEventReports() {
      this.loading = true;

      try {
        var snapshot = await portalsApp
          .firestore()
          .collection("eventReports")
          .get();

        var rows = [];

        snapshot.forEach((doc) => {
          var data = doc.data() || {};

          var organizationState =
            data.organizationState ||
            data.organizationAddressState ||
            this.getStateFromAddress(data.organizationAddress);

          rows.push({
            id: doc.id,
            eventDate: data.eventDate || data.dateCreated || "",
            eventName: data.eventName || "",
            organizationName: data.organizationName || "Unknown",
            organizationAddress: data.organizationAddress || "",
            organizationState: organizationState,
            //organizationType: data.organizationType || "Unknown",
            eventReportTotal: this.toNumber(data.eventReportTotal),
            numberOfWinners: this.toNumber(data.numberOfWinners),
          });
        });

        this.eventRows = rows;
      } catch (error) {
        console.error("Error loading organization insights:", error);

        if (this.$store) {
          this.$store.dispatch("setSnackbar", {
            display: true,
            color: "error",
            text: "Unable to load organization insights",
            timeout: 5000,
          });
        }
      }

      this.loading = false;
    },

    getStateFromAddress(address) {
      if (!address) return "Unknown";

      var text = String(address)
        .toUpperCase()
        .replace(/,/g, " ")
        .replace(/\./g, " ");

      var states = [
        { abbr: "AL", name: "ALABAMA" },
        { abbr: "AK", name: "ALASKA" },
        { abbr: "AZ", name: "ARIZONA" },
        { abbr: "AR", name: "ARKANSAS" },
        { abbr: "CA", name: "CALIFORNIA" },
        { abbr: "CO", name: "COLORADO" },
        { abbr: "CT", name: "CONNECTICUT" },
        { abbr: "DE", name: "DELAWARE" },
        { abbr: "FL", name: "FLORIDA" },
        { abbr: "GA", name: "GEORGIA" },
        { abbr: "HI", name: "HAWAII" },
        { abbr: "ID", name: "IDAHO" },
        { abbr: "IL", name: "ILLINOIS" },
        { abbr: "IN", name: "INDIANA" },
        { abbr: "IA", name: "IOWA" },
        { abbr: "KS", name: "KANSAS" },
        { abbr: "KY", name: "KENTUCKY" },
        { abbr: "LA", name: "LOUISIANA" },
        { abbr: "ME", name: "MAINE" },
        { abbr: "MD", name: "MARYLAND" },
        { abbr: "MA", name: "MASSACHUSETTS" },
        { abbr: "MI", name: "MICHIGAN" },
        { abbr: "MN", name: "MINNESOTA" },
        { abbr: "MS", name: "MISSISSIPPI" },
        { abbr: "MO", name: "MISSOURI" },
        { abbr: "MT", name: "MONTANA" },
        { abbr: "NE", name: "NEBRASKA" },
        { abbr: "NV", name: "NEVADA" },
        { abbr: "NH", name: "NEW HAMPSHIRE" },
        { abbr: "NJ", name: "NEW JERSEY" },
        { abbr: "NM", name: "NEW MEXICO" },
        { abbr: "NY", name: "NEW YORK" },
        { abbr: "NC", name: "NORTH CAROLINA" },
        { abbr: "ND", name: "NORTH DAKOTA" },
        { abbr: "OH", name: "OHIO" },
        { abbr: "OK", name: "OKLAHOMA" },
        { abbr: "OR", name: "OREGON" },
        { abbr: "PA", name: "PENNSYLVANIA" },
        { abbr: "RI", name: "RHODE ISLAND" },
        { abbr: "SC", name: "SOUTH CAROLINA" },
        { abbr: "SD", name: "SOUTH DAKOTA" },
        { abbr: "TN", name: "TENNESSEE" },
        { abbr: "TX", name: "TEXAS" },
        { abbr: "UT", name: "UTAH" },
        { abbr: "VT", name: "VERMONT" },
        { abbr: "VA", name: "VIRGINIA" },
        { abbr: "WA", name: "WASHINGTON" },
        { abbr: "WV", name: "WEST VIRGINIA" },
        { abbr: "WI", name: "WISCONSIN" },
        { abbr: "WY", name: "WYOMING" },
      ];

      for (var i = 0; i < states.length; i++) {
        var state = states[i];

        var abbrRegex = new RegExp("(^|\\s)" + state.abbr + "(\\s|$)");
        var nameRegex = new RegExp("(^|\\s)" + state.name + "(\\s|$)");

        if (abbrRegex.test(text) || nameRegex.test(text)) {
          return state.abbr;
        }
      }

      return "Unknown";
    },

    parseDate(value) {
      if (!value) return null;

      var parsed = moment(
        value,
        [
          "YYYY-MM-DD",
          "MM-DD-YYYY",
          "M-D-YYYY",
          "MM/DD/YYYY",
          "M/D/YYYY",
          moment.ISO_8601,
        ],
        true
      );

      if (!parsed.isValid()) {
        parsed = moment(value);
      }

      return parsed;
    },

    toNumber(value) {
      if (value === undefined || value === null || value === "") return 0;

      if (typeof value === "string") {
        value = value.replace("$", "").replace(/,/g, "").trim();
      }

      var number = Number(value);

      if (isNaN(number)) return 0;

      return number;
    },

    formatCurrency(value) {
      return this.toNumber(value).toLocaleString("en-US", {
        style: "currency",
        currency: "USD",
      });
    },

    formatDate(value) {
      if (!value) return "";

      var parsed = this.parseDate(value);

      if (!parsed || !parsed.isValid()) return value;

      return parsed.format("MMM D, YYYY");
    },
  },
};
</script>